import React, {useState} from "react";
import {Modal} from "components";
import {useTranslation} from "react-i18next";
import {LANGUAGE_PATH} from "modules";
import styled from "styled-components";
import {ReactComponent as QatarLogo} from "assets/images/logos/qatarLogo.svg";
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
} from "@mui/material";
import {Datepicker} from "apps/DRAW_PREDICTOR/components/Datepicker";
import {useDispatch, useSelector} from "react-redux";
import {getSupportedCountries, showRegistration} from "apps/DRAW_PREDICTOR/modules/selectors";
import {NavLink} from "react-router-dom";
import {PrimaryButton} from "apps/DRAW_PREDICTOR/components/Buttons";
import {handleModalState} from "apps/DRAW_PREDICTOR/modules/actions/modals";
import {IDateProps} from "apps/DRAW_PREDICTOR/modules/types";

const ModalInner = styled.div`
	text-align: center;
	padding: 20px;
	h2 {
		font-family: "Qatar2022";
		font-style: normal;
		font-weight: bold;
		font-size: 25px;
		line-height: 120%;
		margin: 30px auto 10px;
		max-width: 460px;
	}
	h3 {
		max-width: 500px;
		font-family: "Graphik";
		font-style: normal;
		font-weight: normal;
		font-size: 18px;
		line-height: 160%;
		margin: 0 auto 20px;
	}
`;

const LabelLink = styled.p`
	font-family: "Poppins";
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 160%;
	a {
		text-decoration: underline;
	}
`;

export const RegistrationModal: React.FC = () => {
	const {t} = useTranslation(LANGUAGE_PATH);
	const countries = useSelector(getSupportedCountries);
	const [selectedCountry, setSelectedCountry] = useState("");
	const show = useSelector(showRegistration);
	const dispatch = useDispatch();

	const handleCountryChange = (event: SelectChangeEvent<string>) => {
		setSelectedCountry(event.target.value);
	};

	const onClose = () => {
		dispatch(handleModalState({registration: false}));
	};

	const onChange = () => {
		console.log("changed");
	};

	const onDateChange = ({dobDay, dobMonth, dobYear}: IDateProps) => {
		console.log({dobDay, dobMonth, dobYear} as IDateProps);
	};

	const sponsorLabel = t("modal.welcome.terms") || "";

	if (!show) {
		return null;
	}

	return (
		<Modal onClose={onClose}>
			<ModalInner>
				<QatarLogo />
				<h2>{t("register.form.header")}</h2>

				<form onChange={onChange}>
					<FormControl sx={{m: 1, minWidth: 380}}>
						<TextField
							variant="standard"
							required={true}
							error={false}
							label={t("register.form.email")}
							placeholder={t("register.form.email_placeholder")}
							name="firstName"
						/>
					</FormControl>
					<FormControl variant="standard" sx={{m: 1, minWidth: 380}}>
						<TextField
							variant="standard"
							required={true}
							error={false}
							type="password"
							label={t("register.form.password")}
							placeholder={t("register.form.password_placeholder")}
							name="password"
						/>
					</FormControl>
					<FormControl variant="standard" sx={{m: 1, minWidth: 380}}>
						<TextField
							variant="standard"
							required={true}
							error={false}
							type="password"
							label={t("register.form.confirm_password")}
							placeholder={t("register.form.confirm_password_placeholder")}
							name="confirmPassword"
						/>
					</FormControl>
					<FormControl variant="standard" sx={{m: 1, minWidth: 380}}>
						<Datepicker onDateChange={onDateChange} />
					</FormControl>
					<FormControl variant="standard" sx={{m: 1, minWidth: 380}}>
						<InputLabel id="supported-country-label">
							{t("modal.welcome.dropdown_header")}
						</InputLabel>
						<Select
							labelId="supported-country-label"
							id="supported-country"
							value={selectedCountry}
							onChange={handleCountryChange}
							label={t("modal.welcome.dropdown_header")}>
							<MenuItem value="">
								<em>{t("modal.welcome.dropdown_text")}</em>
							</MenuItem>
							{countries.map((country) => (
								<MenuItem key={country.key} value={country.key}>
									{country.value}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl sx={{m: 3}} component="fieldset" variant="standard">
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										sx={{"& .MuiSvgIcon-root": {fontSize: 34}}}
										name="terms"
									/>
								}
								label={
									<LabelLink>
										<NavLink to="/help/guidelines">
											{t("modal.welcome.terms")}
										</NavLink>
									</LabelLink>
								}
							/>
						</FormGroup>
					</FormControl>
					<FormControl sx={{m: 3}} component="fieldset" variant="standard">
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										sx={{"& .MuiSvgIcon-root": {fontSize: 34}}}
										name="sponsor"
									/>
								}
								label={sponsorLabel}
							/>
						</FormGroup>
					</FormControl>
					<PrimaryButton>{t("register.form.register")}</PrimaryButton>
				</form>
			</ModalInner>
		</Modal>
	);
};
